import React, { useMemo, useState } from 'react';
import styles from './ProfileSettings.module.scss';
import { ReactComponent as Logo } from '../../assets/icons/fa.svg';
import UniversalModal from '../UniversalModal/UniversalModal';

const TABS = [
  { id: 'name', label: 'Смена имени' },
  { id: 'password', label: 'Смена пароля' },
];

const ProfileSettings = ({ currentUser, trigger = 'Настройки', triggerClassName = '' }) => {
  const [activeTab, setActiveTab] = useState('name');
  const [pendingTab, setPendingTab] = useState('name');
  const [isOpen, setIsOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: currentUser?.name || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [touchedFields, setTouchedFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touchedFields[name]) {
      setTouchedFields((prev) => ({ ...prev, [name]: false }));
    }

    if (serverError) setServerError('');
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
  };

  const isNameValid = useMemo(() => {
    const name = formData.name.trim();
    return (
      /^[a-zA-Zа-яА-ЯёЁ\s]+$/.test(name) &&
      name.length >= 2 &&
      name.length <= 40
    );
  }, [formData.name]);

  const passwordCriteria = useMemo(() => {
    const pass = formData.newPassword;
    return [
      { id: 'length', label: 'От 8 символов', isValid: pass.length >= 8 },
      { id: 'number', label: 'Хотя бы одна цифра', isValid: /[0-9]/.test(pass) },
      { id: 'special', label: 'Спец. символ', isValid: /[^a-zA-Zа-яА-Я0-9\s]/.test(pass) },
    ];
  }, [formData.newPassword]);

  const passwordStrength = useMemo(() => {
    const pass = formData.newPassword;
    if (!pass) return 'none';
    if (pass.length < 8) return 'weak';

    let score = 0;
    if (/[a-zа-я]/i.test(pass)) score++;
    if (/[0-9]/.test(pass)) score++;
    if (/[^a-zA-Zа-яА-Я0-9\s]/.test(pass)) score++;

    if (score >= 3 && pass.length >= 10) return 'strong';
    if (score >= 2) return 'medium';
    return 'weak';
  }, [formData.newPassword]);

  const isPassMatch =
    formData.newPassword === formData.confirmPassword &&
    formData.confirmPassword !== '';

  const isNameTab = activeTab === 'name';

  const canSubmitName = isNameValid && formData.name.trim() !== currentUser?.name;
  const canSubmitPassword =
    formData.currentPassword.length > 0 &&
    passwordStrength !== 'weak' &&
    isPassMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isNameTab && !canSubmitName) return;
    if (!isNameTab && !canSubmitPassword) return;

    setLoading(true);
    setServerError('');

    try {
      const payload = isNameTab
        ? { name: formData.name.trim() }
        : {
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword,
          };

      const res = await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setServerError(data?.error || 'Ошибка при обновлении профиля');
        return;
      }

      alert(isNameTab ? 'Имя успешно изменено!' : 'Пароль успешно изменен!');
      
      if (!isNameTab) {
        setFormData((prev) => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
      } else {
        window.location.reload(); 
      }

      closeModal();
    } catch {
      setServerError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };

  const pendingIndex = useMemo(
    () => TABS.findIndex((t) => t.id === pendingTab),
    [pendingTab]
  );

  const openModal = () => {
    setActiveTab('name');
    setPendingTab('name');
    setFormData({
      name: currentUser?.name || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setTouchedFields({});
    setServerError('');
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const switchTab = (nextId) => {
    if (nextId === pendingTab) return;
    setPendingTab(nextId);
    setActiveTab(nextId);
    setTouchedFields({});
    setServerError('');
  };

  const strengthLabels = {
    none: 'Введите пароль',
    weak: 'Простой',
    medium: 'Средний',
    strong: 'Сложный',
  };

  return (
    <>
      <button type='button' className={triggerClassName} onClick={openModal}>
        {trigger}
      </button>

      <UniversalModal isOpen={isOpen} onClose={closeModal}>
        <div className={styles.settings__content}>
          <div className={styles.settings__tabs}>
            <div
              className={styles.settings__tabIndicator}
              style={{
                transform: `translateX(${pendingIndex * 100}%)`,
              }}
            />
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type='button'
                onClick={() => switchTab(tab.id)}
                disabled={loading}
                className={[
                  styles.settings__tab,
                  pendingTab === tab.id ? styles.settings__tabActive : '',
                ].join(' ')}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className={styles.settings__header}>
            <Logo className={styles.settings__logo} />
            <h1 className={styles.settings__title}>Настройки</h1>
          </div>

          <form
            onSubmit={handleSubmit}
            className={styles.settings__inputs}>
            
            {isNameTab ? (
              <input
                name='name'
                type='text'
                disabled={loading}
                className={[
                  styles.settings__input,
                  touchedFields.name && !isNameValid ? styles.error : '',
                ].join(' ')}
                placeholder='Ваше новое имя'
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
            ) : (
              <>
                <input
                  name='currentPassword'
                  type='password'
                  disabled={loading}
                  className={styles.settings__input}
                  placeholder='Текущий пароль'
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                />

                <div className={styles.passwordWrapper}>
                  <input
                    name='newPassword'
                    type='password'
                    disabled={loading}
                    className={[
                      styles.settings__input,
                      formData.newPassword
                        ? styles[`input--${passwordStrength}`]
                        : '',
                    ].join(' ')}
                    placeholder='Новый пароль'
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={(e) => {
                      handleBlur(e);
                      setIsPasswordFocused(false);
                    }}
                  />

                  {isPasswordFocused && (
                    <div className={styles.passwordTooltip}>
                      <div className={styles.passwordTooltip__list}>
                        {passwordCriteria.map((c) => (
                          <div
                            key={c.id}
                            className={[
                              styles.passwordTooltip__item,
                              c.isValid ? styles.valid : styles.invalid,
                            ].join(' ')}>
                            <span>{c.label}</span>
                          </div>
                        ))}
                      </div>
                      <div
                        className={[
                          styles.passwordTooltip__strength,
                          styles[`strength--${passwordStrength}`],
                        ].join(' ')}>
                        Надежность:{' '}
                        <span>{strengthLabels[passwordStrength]}</span>
                      </div>
                    </div>
                  )}
                </div>

                <input
                  name='confirmPassword'
                  type='password'
                  disabled={loading}
                  className={[
                    styles.settings__input,
                    touchedFields.confirmPassword
                      ? isPassMatch
                        ? styles.success
                        : styles.error
                      : '',
                  ].join(' ')}
                  placeholder='Повторите новый пароль'
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
              </>
            )}

            {serverError && (
              <p className={styles.settings__serverError}>{serverError}</p>
            )}

            <button
              type='submit'
              disabled={
                loading ||
                (isNameTab ? !canSubmitName : !canSubmitPassword)
              }
              className={styles.settings__submit}>
              {loading ? 'Сохранение...' : 'Сохранить изменения'}
            </button>
          </form>
        </div>
      </UniversalModal>
    </>
  );
};

export default ProfileSettings;